import { observer } from 'mobx-react-lite'
import {
  Button,
  Divider,
  Dropdown,
  DropdownCard,
  DropdownContent,
  layoutStore,
  Typography,
} from 'shared/ui'
import { type IParamsContactsOptOut } from 'entities/Contacts/api/types'
import { successOpIinHandler } from 'entities/Contacts/lib/successOpIinHandler'
import { EnrollInCampaignAction } from 'entities/Campaign/ui'
import { BroadcastSendButton } from 'entities/Broadcast'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { WorkflowEnrollAction } from 'widgets/WorkflowEnroll'
import { ChatbotEnrollAction } from 'widgets/ChatbotEnroll'
import { PowerDialerEditorAction } from 'widgets/PowerDialer/ui/PowerDialerEditor'
import { ContactsOptInModalStore } from 'widgets/ContactsOptInModal'
import { useContactsTableContext } from 'pages/contacts/ui/ContactsTable/context/contactsTableContext'

export const LeftContent = observer(() => {
  const contactsTableStore = useContactsTableContext()
  const entities = useEntitiesContext()
  const { userPermissionsStore, usersStore } = entities
  const user = usersStore.user
  const isViewOnlyRole = user?.isViewOnlyRole
  const permissionCanManageCampaigns = userPermissionsStore.getItem('can_manage_campaigns')
  const canManageCampaigns = !Boolean(isViewOnlyRole || !permissionCanManageCampaigns)
  const {
    selectedContacts,
    search,
    total,
    expandedParams,
    contacts,
    activeContact,
    setInActionId,
    loadContacts,
    checkClosePanelAfterDelete,
    tableStore,
    segment,
    selectedModalStore,
    paramsFiltersList,
  } = contactsTableStore
  const { bulkAllMode, selectedIds, resetSelected } = tableStore
  const { openValidateModal, openDeleteModal, deleteContacts } = selectedModalStore
  const { isMobileViewWithoutMSize } = layoutStore

  const deleteCallback = () => {
    setInActionId(null)
  }

  const successDeleteCallback = () => {
    checkClosePanelAfterDelete()
    resetSelected()
    loadContacts()
  }

  const resetInActionId = () => {
    setInActionId(null)
  }

  const opIinHandler = (opt_outs: IParamsContactsOptOut['opt_outs']) => {
    activeContact && successOpIinHandler(activeContact, opt_outs)

    contacts.forEach((contact) => {
      if (bulkAllMode || selectedIds.includes(contact.id)) {
        successOpIinHandler(contact, opt_outs)
      }
    })
  }

  const isRequestAccess = !userPermissionsStore.getItem('can_access_phone_checker')

  const contactsData = {
    search: contactsTableStore.search,
    total: contactsTableStore.total,
    contactsParams: contactsTableStore.expandedParams,
    bulkAll: bulkAllMode,
    selectedIds: selectedIds.map(Number),
  }

  return (
    <>
      <Button
        text='Validate'
        icon='check1'
        typeBtn='contained'
        contained='tertiary'
        size='small'
        onClick={() => {
          openValidateModal({
            bulkAllMode,
            selectedIds: selectedIds as number[],
            search,
            filtersList: segment?.isAllContacts ? paramsFiltersList : segment?.filtersList,
          })
        }}
        disabled={isRequestAccess}
        tooltipProps={
          isRequestAccess
            ? {
                placement: 'top',
                label: [
                  'You don’t have permission',
                  <br key={'br'} />,
                  <Typography
                    ariaLabel={'desc'}
                    key={'desc'}
                    tag={'div'}
                    variant={'body-sm-regular'}
                    color={'var(--content-primary-tertiary-inverted)'}
                    marginTop={2}
                  >
                    Request your Admin
                  </Typography>,
                ],
              }
            : undefined
        }
      />
      <Button
        text='Manage tags'
        icon='tag'
        typeBtn='contained'
        contained='tertiary'
        size='small'
        onClick={() => {
          contactsTableStore.contactsTagsModalStore.onOpen({
            ids: selectedIds as number[],
            isBulkAll: bulkAllMode,
            callback: loadContacts,
            searchParams: expandedParams.search,
            filtersList: expandedParams.filtersList,
          })
        }}
      />
      {!isMobileViewWithoutMSize && (
        <>
          <ChatbotEnrollAction contactsData={contactsData} variant={'button'} />
        </>
      )}
      <Dropdown
        ariaLabel={'LeftActions'}
        iconButtonProps={{ icon: 'moreHorizontal', color: 'tertiary', ariaLabel: 'LeftActions' }}
        placement='bottom-start'
        customComponent={(onCloseMenu) => (
          <DropdownContent>
            {isMobileViewWithoutMSize && (
              <>
                <ChatbotEnrollAction contactsData={contactsData} variant={'dropdownItem'} />
              </>
            )}
            {canManageCampaigns && (
              <EnrollInCampaignAction
                variant={'dropdownItem'}
                onClick={onCloseMenu}
                contactsData={contactsData}
              />
            )}
            <PowerDialerEditorAction
              variant={'dropdownItem'}
              onClick={onCloseMenu}
              contactsData={contactsData}
            />
            <WorkflowEnrollAction
              contactsData={contactsData}
              variant={'dropdownItem'}
              onClick={onCloseMenu}
            />
            <BroadcastSendButton
              contactsData={contactsData}
              variant={'dropdownItem'}
              onClick={onCloseMenu}
            />
            <Divider sx={{ marginY: '5px', marginX: '-4px' }} />
            <DropdownCard
              item={{
                id: 0,
                label: 'Opt-in status',
                iconL: 'shield',
                onClick: () => {
                  new ContactsOptInModalStore(entities).handleOpen(selectedContacts, {
                    reset: resetInActionId,
                    successHandler: opIinHandler,
                    isBulkAll: bulkAllMode,
                    filtersList: expandedParams.filtersList,
                    search,
                  })
                  onCloseMenu()
                },
              }}
            />
            <Divider sx={{ marginY: '5px', marginX: '-4px' }} />
            <DropdownCard
              item={{
                id: 1,
                label: 'Delete',
                iconL: 'delete',
                onClick: () => {
                  const selectedIdsLength = bulkAllMode ? total : selectedIds.length

                  openDeleteModal({
                    selectedIdsLength,
                    primaryCallback: () => {
                      deleteContacts({
                        bulkAllMode,
                        ids: selectedIds as number[],
                        search,
                        filtersList: expandedParams?.filtersList,
                        successCallback: successDeleteCallback,
                        finallyCallback: deleteCallback,
                      })
                    },
                    onCloseCallback: resetInActionId,
                  })
                  onCloseMenu()
                },
              }}
            />
          </DropdownContent>
        )}
      />
    </>
  )
})
